import React, { useState, useEffect, Fragment } from 'react';
import { Transition } from '@headlessui/react';
import { fetchWithAuth } from '../../utils/apiHelper';
import ModalHeader from './ModalHeader';
import CajaInfo from './CajaInfo';
import OperationsTable from './OperationsTable';
import PaymentsTable from './PaymentsTable';
import SenasTable from './SenasTable';
import GiftCardsTable from './GiftCardsTable';

const OperacionesPagosModal = ({ isOpen, onRequestClose }) => {
  const [data, setData] = useState({
    operaciones: [],
    pagos: [],
    senas: [],
    giftCards: [] // Agregamos giftCards en el state
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalEnCaja, setTotalEnCaja] = useState(0);
  const [maxCajaAmount, setMaxCajaAmount] = useState(0);
  const [alertVisible, setAlertVisible] = useState(false);

  // Estado para manejar items seleccionados (cualquier tipo de id)
  const [selectedItems, setSelectedItems] = useState(new Set());

  useEffect(() => {
    if (isOpen) {
      const fetchData = async () => {
        try {
          // 1) Obtener datos de operaciones, pagos, senas y gift cards de hoy
          const result = await fetchWithAuth('/operaciones/operationsypagos');
          setData(result);
          setLoading(false);
        } catch (err) {
          setError(err.message);
          setLoading(false);
        }
      };

      const fetchMontoActualEnCaja = async () => {
        try {
          const result = await fetchWithAuth('/caja/montoencaja');
          setTotalEnCaja(result.total_en_caja);
        } catch (err) {
          console.error('Error fetching monto actual en caja:', err);
        }
      };

      const fetchMaxCajaAmount = async () => {
        try {
          const result = await fetchWithAuth('/caja/max/amount');
          setMaxCajaAmount(result.max_amount);
        } catch (err) {
          console.error('Error fetching max caja amount:', err);
        }
      };

      fetchData();
      fetchMontoActualEnCaja();
      fetchMaxCajaAmount();
    }
  }, [isOpen]);

  // Mostrar alerta si el monto en caja supera el máximo permitido
  useEffect(() => {
    setAlertVisible(totalEnCaja > maxCajaAmount);
  }, [totalEnCaja, maxCajaAmount]);

  if (!isOpen) return null;

  // Manejo de selección de checkbox
  const handleCheckboxChange = (id) => {
    setSelectedItems((prevSelectedItems) => {
      const newSelectedItems = new Set(prevSelectedItems);
      if (newSelectedItems.has(id)) {
        newSelectedItems.delete(id);
      } else {
        newSelectedItems.add(id);
      }
      return newSelectedItems;
    });
  };

  // Ejemplo de función para "marcar como completado"
  const handleMarkCompleted = () => {
    // Aquí iría la lógica para marcar items como completados, por ejemplo enviando una petición a la API.
    setSelectedItems(new Set());
  };

  // Función para formatear la hora (por ejemplo, para mostrar 2 dígitos para hora y minutos)
  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    return new Date(`1970-01-01T${timeString}`).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const excessAmount = totalEnCaja - maxCajaAmount;

  return (
    <Transition show={isOpen} as={Fragment}>
  <div className="fixed z-50 inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center">
    <Transition.Child
      as={Fragment}
      enter="transition-opacity ease-in duration-300"
      enterFrom="opacity-0"
      enterTo="opacity-100"
      leave="transition-opacity ease-out duration-300"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      <div className="fixed inset-0"></div>
    </Transition.Child>

    <Transition.Child
      as={Fragment}
      enter="transition ease-out duration-300 transform"
      enterFrom="scale-95 opacity-0 translate-y-4"
      enterTo="scale-100 opacity-100 translate-y-0"
      leave="transition ease-in duration-200 transform"
      leaveFrom="scale-100 opacity-100 translate-y-0"
      leaveTo="scale-95 opacity-0 translate-y-4"
    >
      <div className="relative w-[75vw] h-[90vh] bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-2xl shadow-2xl p-6 sm:p-8 flex flex-col overflow-hidden text-gray-200 border border-gray-700">
        {/* Header + Caja Info */}
        <div className="flex-shrink-0 space-y-4">
          <ModalHeader onRequestClose={onRequestClose} />

          <CajaInfo
            totalEnCaja={totalEnCaja}
            maxCajaAmount={maxCajaAmount}
            excessAmount={excessAmount}
            alertVisible={alertVisible}
          />
        </div>

        {/* Scrollable Content */}
        <div className="flex-grow overflow-y-auto mt-4 space-y-6 pr-1">
          {loading ? (
            <div className="flex justify-center items-center h-40">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-500 border-t-transparent"></div>
            </div>
          ) : error ? (
            <div className="bg-red-500 bg-opacity-20 text-red-400 text-center p-4 rounded-lg text-lg font-medium border border-red-500">
              Error: {error}
            </div>
          ) : (
            <>
              <OperationsTable
                operations={data.operaciones}
                selectedItems={selectedItems}
                handleCheckboxChange={handleCheckboxChange}
                formatTime={formatTime}
              />
              <PaymentsTable
                pagos={data.pagos}
                selectedItems={selectedItems}
                handleCheckboxChange={handleCheckboxChange}
                formatTime={formatTime}
              />
              <SenasTable
                senas={data.senas}
                selectedItems={selectedItems}
                handleCheckboxChange={handleCheckboxChange}
                formatTime={formatTime}
              />
              <GiftCardsTable
                giftCards={data.giftCards}
                selectedItems={selectedItems}
                handleCheckboxChange={handleCheckboxChange}
                formatTime={formatTime}
              />
            </>
          )}
        </div>

        {/* Button */}
        {!loading && !error && (
          <div className="flex-shrink-0 pt-4 flex justify-end">
            <button
              onClick={handleMarkCompleted}
              className="px-5 py-3 bg-gradient-to-r from-indigo-500 to-blue-600 hover:from-indigo-600 hover:to-blue-700 focus:ring-4 focus:ring-indigo-400 text-white font-semibold rounded-lg transition-all transform hover:scale-105 focus:outline-none shadow-lg"
            >
              Marcar como Completado
            </button>
          </div>
        )}
      </div>
    </Transition.Child>
  </div>
</Transition>


  );
};

export default OperacionesPagosModal;
